(() => {
  const read = (node, fallback) => {
    try { return JSON.parse(node?.textContent || ''); }
    catch { return fallback; }
  };
  const pageCache = new Map();
  let active;
  let revision = 0;
  let pending;
  let pendingMode;
  const updateGalleryOffset = () => {
    if (!active?.root) return;
    const height = document.querySelector('m-header .m-header__wrapper')?.getBoundingClientRect().height || document.querySelector('m-header')?.getBoundingClientRect().height || 80;
    active.root.style.setProperty('--ca-gallery-top', Math.ceil(height + 16) + 'px');
  };
  window.addEventListener('resize', updateGalleryOffset, { passive: true });
  const productMeta = (page) => {
    const script = Array.from(page.scripts).find((node) => node.textContent.includes('window.ShopifyAnalytics.meta[attr] = meta[attr]'));
    const text = script?.textContent || '';
    const assignment = text.indexOf('var meta = ');
    if (assignment < 0) return null;
    const start = text.indexOf('{', assignment);
    let depth = 0, quoted = false, escaped = false;
    for (let i = start; i >= 0 && i < text.length; i += 1) {
      const char = text[i];
      if (quoted) {
        if (escaped) escaped = false;
        else if (char === '\\') escaped = true;
        else if (char === '"') quoted = false;
      } else if (char === '"') quoted = true;
      else if (char === '{') depth += 1;
      else if (char === '}' && --depth === 0) {
        try { return JSON.parse(text.slice(start, i + 1)); } catch { return null; }
      }
    }
    return null;
  };
  const pageKey = (href) => {
    const url = new URL(href, location.href);
    url.hash = '';
    url.searchParams.delete('variant');
    return url.href;
  };
  const loadPage = async (href, expectedId, signal) => {
    const url = new URL(href, location.href);
    if (url.origin !== location.origin) throw new Error('Invalid destination');
    const key = pageKey(url.href);
    const cached = pageCache.get(key);
    if (cached?.expires > Date.now() && cached.identity.id === expectedId) return cached;
    url.hash = '';
    const response = await fetch(url, { credentials: 'same-origin', signal });
    if (!response.ok || new URL(response.url).origin !== location.origin) throw new Error('Product unavailable');
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = page.querySelector('[data-ca-cwf-production]');
    const identity = read(root?.querySelector('[data-ca-navigation-current]'), null);
    if (!root || identity?.id !== expectedId || identity.familyId !== active.current.familyId ||
        identity.design !== active.current.design || !identity.variants?.length) throw new Error('Product mismatch');
    const result = { page, identity, expires: Date.now() + 30000 };
    pageCache.set(key, result);
    if (pageCache.size > 10) pageCache.delete(pageCache.keys().next().value);
    return result;
  };
  const setBusy = (root, busy) => {
    root.dataset.caSizeLoading = String(busy);
    root.querySelector('[data-ca-size-picker]')?.setAttribute('aria-busy', String(busy));
    const button = root.querySelector('[data-ca-pdp-add]');
    const selected = root.querySelector('[data-ca-production-variant-input]')?.value;
    const variant = read(root.querySelector('[data-ca-production-variants]'), []).find((v) => String(v.id) === selected);
    if (button) button.disabled = busy || !variant?.available;
  };
  const cancel = () => {
    if (pending && pendingMode === 'pop' && active) {
      history.replaceState({ ...history.state, caCwfProductId: active.current.id }, '', active.shownUrl);
    }
    revision += 1;
    pending?.abort();
    pending = null;
    pendingMode = null;
    if (active) setBusy(active.root, false);
  };
  const cloneMarkup = (node) => {
    if (!node) return null;
    const copy = document.importNode(node, true);
    copy.querySelectorAll('script:not([type="application/json"]):not([type="application/ld+json"])').forEach((script) => script.remove());
    return copy;
  };
  const navigate = async (href, expectedId, mode = 'push', keyboard = false) => {
    if (!active || typeof window.caCwfInitializeProduct !== 'function') return;
    cancel();
    const attempt = revision;
    const previous = active;
    const controller = new AbortController();
    pending = controller;
    pendingMode = mode;
    const timer = setTimeout(() => controller.abort(), 15000);
    const url = new URL(href, location.href);
    setBusy(previous.root, true);
    previous.root.querySelector('[data-ca-size-error]')?.remove();
    try {
      const { page, identity } = await loadPage(url.href, expectedId, controller.signal);
      if (attempt !== revision || !previous.root.isConnected) return;
      const selectedId = url.searchParams.get('variant');
      const variant = identity.variants.find((item) => item.id === selectedId);
      const next = cloneMarkup(page.querySelector('[data-ca-cwf-production]'));
      const form = next?.querySelector('form[data-product-id]');
      const variants = read(next?.querySelector('[data-ca-production-variants]'), []);
      const meta = productMeta(page);
      if (!variant || form?.dataset.productId !== identity.id ||
          !variants.some((item) => String(item.id) === selectedId) ||
          !next.querySelector('[data-ca-production-media]')) throw new Error('Incomplete product');
      if (window.ShopifyAnalytics?.meta && String(meta?.product?.id) !== identity.id) throw new Error('Incomplete product metadata');
      const picker = previous.root.querySelector('[data-ca-size-picker]');
      const oldY = picker?.getBoundingClientRect().top;
      const quantity = previous.root.querySelector('[data-ca-pdp-quantity]')?.value;
      window.caCwfInitializeProduct(next, selectedId);
      if (next.querySelector('[data-ca-production-variant-input]')?.value !== selectedId) throw new Error('Variant mismatch');
      if (quantity && next.querySelector('[data-ca-pdp-quantity]')) next.querySelector('[data-ca-pdp-quantity]').value = quantity;
      // Commit only after the complete destination and native form agree.
      previous.root.replaceWith(next);
      try {
        const seen = JSON.parse(localStorage.getItem('minimog-recently-viewed') || '[]');
        const ids = [Number(identity.id), Number(previous.current.id), ...seen].filter(Number.isSafeInteger);
        localStorage.setItem('minimog-recently-viewed', JSON.stringify([...new Set(ids)].slice(0, 20)));
      } catch {}
      for (const selector of ['.ca-cwf-v7-reviews', 'm-recently-viewed']) {
        const oldWidget = document.querySelector(selector);
        const newWidget = cloneMarkup(page.querySelector(selector));
        if (oldWidget && newWidget) {
          oldWidget.swiper?.destroy?.(true, true);
          oldWidget.replaceWith(newWidget);
        }
      }
      next.caCwfInitializePageChrome?.();
      if (meta && window.ShopifyAnalytics?.meta) {
        window.ShopifyAnalytics.meta.product = meta.product;
        window.ShopifyAnalytics.meta.page = meta.page;
        if (window.meta?.product) { window.meta.product = meta.product; window.meta.page = meta.page; }
      }
      document.title = page.title;
      for (const selector of ['link[rel="canonical"]', 'meta[name="description"]', 'meta[property^="og:"]', 'meta[name^="twitter:"]']) {
        document.head.querySelectorAll(selector).forEach((node) => node.remove());
        page.head.querySelectorAll(selector).forEach((node) => document.head.append(document.importNode(node, true)));
      }
      if (mode === 'push') history.pushState({ ...history.state, caCwfProductId: identity.id }, '', url.href);
      else history.replaceState({ ...history.state, caCwfProductId: identity.id }, '', url.href);
      await initialize(previous.products());
      if (attempt !== revision) return;
      if (Number.isFinite(oldY)) {
        const newY = next.querySelector('[data-ca-size-picker]')?.getBoundingClientRect().top;
        if (Number.isFinite(newY)) window.scrollBy({ top: newY - oldY, behavior: 'instant' });
      }
      if (keyboard) next.querySelector('[data-ca-size-link][aria-current="page"]')?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('ca:cwf:product-change', { detail: { productId: identity.id, variantId: selectedId } }));
    } catch (error) {
      if (attempt !== revision) return;
      if (mode === 'pop') history.replaceState({ ...history.state, caCwfProductId: previous.current.id }, '', previous.shownUrl);
      const message = document.createElement('p');
      message.dataset.caSizeError = '';
      message.setAttribute('role', 'alert');
      message.textContent = previous.root.dataset.unavailable || 'Unavailable';
      previous.root.querySelector('[data-ca-size-picker]')?.after(message);
    } finally {
      clearTimeout(timer);
      if (attempt === revision) {
        pending = null;
        pendingMode = null;
        if (active) setBusy(active.root, false);
      }
    }
  };
  const initialize = async (knownProducts = []) => {
    const root = document.querySelector('[data-ca-cwf-production]');
    const family = document.querySelector('[data-ca-v7-9-family]');
    if (!root || root.dataset.caNavigationStarted === 'true') return;
    root.dataset.caNavigationStarted = 'true';
    const current = read(root.querySelector('[data-ca-navigation-current]'), null);
    if (!current?.id || !current.familyId) return;
    const sizePicker = root.querySelector('[data-ca-size-picker]');
    const familyGrid = family?.querySelector('.ca-cwf-v7-9-family__grid');
    const selectors = read(root.querySelector('[data-ca-selector-media]'), {});
    const currentUrl = new URL(window.location.href);
    const preview = currentUrl.pathname.endsWith('/products_preview') &&
      currentUrl.searchParams.has('preview_key');
    let previewEntries = [];
    if (preview) {
      const value = new URLSearchParams(currentUrl.hash.slice(1)).get('cwf-preview') || '';
      const entries = value.split(',').map((part) => part.split(':'));
      if (entries.length <= 20 && entries.every(([id, key, variantId, extra]) =>
        /^\d+$/.test(id || '') && /^[a-f0-9]{32}$/.test(key || '') &&
        /^\d+$/.test(variantId || '') && !extra
      ) && new Set(entries.map(([id]) => id)).size === entries.length &&
        entries.some(([id, key]) => id === current.id &&
          key === currentUrl.searchParams.get('preview_key'))) {
        previewEntries = entries;
      }
    }
    const previewKeys = new Map(previewEntries);
    const previewHash = previewEntries.length
      ? '#cwf-preview=' + previewEntries.map((entry) => entry.join(':')).join(',')
      : '';
    let products = read(family?.querySelector('[data-ca-navigation-family]'), []);
    products = products.filter((item) => item?.familyId === current.familyId);
    if (!products.some((item) => item.id === current.id)) products.push(current);
    if (knownProducts.length) products = knownProducts.filter((item) => item.familyId === current.familyId).map((item) => item.id === current.id ? current : item);
    active = { root, current, products: () => products, shownUrl: location.href };
    updateGalleryOffset();

    const chosenOption = () => {
      const id = root.querySelector('[data-ca-production-variant-input]')?.value;
      return current.variants.find((variant) => variant.id === id)?.option ||
        current.variants[0]?.option;
    };
    const variantFor = (item) =>
      item.variants.find((variant) => variant.option === chosenOption()) || item.variants[0];
    const destination = (item, variant) => {
      if (preview && previewKeys.has(item.id)) {
        const url = new URL(currentUrl);
        url.searchParams.set('preview_key', previewKeys.get(item.id));
        url.searchParams.set('variant', variant.id);
        url.hash = previewHash;
        return url.href;
      }
      if (preview && item.id === current.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        return url.href;
      }
      const url = new URL(variant.url, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      for (const name of ['preview_theme_id', 'view', 'locale', 'country', 'currency']) {
        if (currentUrl.searchParams.has(name))
          url.searchParams.set(name, currentUrl.searchParams.get(name));
      }
      return url.href;
    };
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const image = (variant, className) => {
      const node = element('img', className);
      if (!variant.image) return null;
      const source = new URL(variant.image, window.location.origin);
      if (source.protocol !== 'https:') return null;
      node.src = source.href;
      node.alt = variant.alt || '';
      node.width = 720;
      node.height = 720;
      node.loading = 'lazy';
      node.decoding = 'async';
      return node;
    };
    const selectorImage = (node, variant, fill) => {
      const data = selectors[variant.id];
      if (!node || !data?.url || !(data.extent > 0)) return;
      node.src = data.url;
      node.removeAttribute('srcset');
      node.removeAttribute('sizes');
      node.classList.add('ca-cwf-selector-image');
      node.style.setProperty('--ca-selector-scale', String(fill / data.extent));
      node.style.setProperty('--ca-selector-x', -data.x + '%');
      node.style.setProperty('--ca-selector-y', -data.y + '%');
      node.parentElement.dataset.caSelectorFrame = '';
    };
    root.querySelectorAll('[data-ca-production-variant]').forEach((button) => {
      const variant = current.variants.find((v) => v.id === button.dataset.variantId);
      if (variant) selectorImage(button.querySelector('img'), variant, .78);
    });
    const addDestination = (link, item, variant) => {
      const url = destination(item, variant);
      if (!url) return false;
      link.href = url;
      link.dataset.productId = item.id;
      link.dataset.variantId = variant.id;
      link.dataset.accentValue = variant.option;
      return true;
    };
    const render = () => {
      if (sizePicker) {
        const siblings = products.filter((item) => item.design === current.design);
        const maxDiameter = Math.max(...siblings.map((item) => selectors[item.variants[0]?.id]?.diameter || 0));
        const nodes = siblings.map((item) => {
          const variant = variantFor(item);
          if (!variant) return null;
          const link = element('a', 'ca-cwf-pdp__size');
          if (!addDestination(link, item, variant)) return null;
          link.dataset.caSizeLink = '';
          if (item.id === current.id) link.setAttribute('aria-current', 'page');
          const frame = element('span', 'ca-cwf-pdp__size-frame');
          const media = image(variant, 'ca-cwf-pdp__size-image');
          if (media) {
            frame.append(media);
            const diameter = selectors[variant.id]?.diameter;
            if (diameter && maxDiameter) selectorImage(media, variant, .82 * diameter / maxDiameter);
          }
          const copy = element('span', 'ca-cwf-pdp__size-copy');
          const [label, dimensions] = (item.size || '').split(' · ');
          copy.append(element('strong', '', label || item.title));
          if (dimensions) copy.append(element('span', '', dimensions));
          link.append(frame, copy);
          return link;
        }).filter(Boolean);
        sizePicker.replaceChildren(...nodes);
        sizePicker.dataset.optionCount = String(nodes.length);
      }
      if (familyGrid) {
        const cards = products.filter((item) => item.id !== current.id && item.design && item.design !== current.design).map((item, index) => {
          const alternatives = item.variants.filter((variant) => variant.option !== chosenOption());
          const variant = alternatives[index % alternatives.length];
          if (!variant) return null;
          const card = element('article', 'ca-cwf-v7-9-family__card');
          const link = element('a', 'ca-cwf-v7-9-family__link');
          if (!addDestination(link, item, variant)) return null;
          const media = element('span', 'ca-cwf-v7-9-family__media');
          const picture = image(variant, '');
          if (picture) media.append(picture);
          link.append(media, element('h3', '', item.design));
          link.append(element('p', 'ca-cwf-v7-9-family__size', item.size || ''));
          link.append(element('p', 'ca-cwf-v7-9-family__accent', root.dataset.caCopyLanguage === 'en' ? variant.option.replace(/ Accent$/, '') : variant.option));
          link.append(element('p', 'ca-cwf-v7-9-family__price', variant.price));
          card.append(link);
          return card;
        }).filter(Boolean).slice(0, 3);
        familyGrid.replaceChildren(...cards);
        family.hidden = cards.length === 0;
      }
    };
    const onVariant = (event) => {
      if (active?.root !== root) return;
      cancel();
      const url = new URL(active.shownUrl);
      url.searchParams.set('variant', event.detail.variantId);
      history.replaceState(history.state, '', url.href);
      root.querySelector('[data-ca-size-error]')?.remove();
      active.shownUrl = location.href;
      render();
    };
    root.addEventListener('ca:cwf:variant-change', onVariant);
    render();

    if (previewEntries.length && !knownProducts.length) {
      root.dataset.caNavigationState = 'loading';
      sizePicker?.setAttribute('aria-busy', 'true');
      const context = [current.familyId, window.Shopify?.theme?.id,
        document.documentElement.lang, window.Shopify?.country,
        window.Shopify?.currency?.active, currentUrl.searchParams.get('view') || '',
        previewEntries.map(([id]) => id).join(',')].join('|');
      const cacheKey = 'ca-cwf-navigation-v1:' + context;
      let cached;
      try { cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null'); }
      catch {}
      const validProduct = (item, id) => item?.id === id &&
        item.familyId === current.familyId && item.variants?.length > 0;
      const fetchProduct = async ([id, key, variantId]) => {
        if (id === current.id) return current;
        const saved = cached?.expires > Date.now() &&
          cached.products?.find((item) => validProduct(item, id));
        if (saved) return saved;
        const url = new URL(currentUrl);
        url.hash = '';
        url.searchParams.set('preview_key', key);
        url.searchParams.set('variant', variantId);
        const response = await fetch(url, {
          credentials: 'same-origin',
          signal: AbortSignal.timeout(15000)
        });
        if (!response.ok) throw new Error('Preview unavailable');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const item = read(page.querySelector('[data-ca-navigation-current]'), null);
        if (!validProduct(item, id)) throw new Error('Preview product mismatch');
        return item;
      };
      const settled = await Promise.allSettled(previewEntries.map(fetchProduct));
      products = settled.filter((item) => item.status === 'fulfilled').map((item) => item.value);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          expires: Date.now() + 5 * 60 * 1000,
          products
        }));
      } catch {}
      render();
      sizePicker?.removeAttribute('aria-busy');
      root.dataset.caNavigationState = settled.every((item) => item.status === 'fulfilled')
        ? 'ready' : 'partial';
    } else {
      root.dataset.caNavigationState = 'ready';
    }
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-ca-size-link]');
    if (!link || !active?.root.contains(link) || event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download')) return;
    if (typeof window.caCwfInitializeProduct !== 'function') return;
    event.preventDefault();
    if (link.dataset.productId === active.current.id) { cancel(); return; }
    navigate(link.href, link.dataset.productId, 'push', event.detail === 0);
  });
  const prefetch = (event) => {
    const link = event.target.closest?.('[data-ca-size-link]');
    if (!link || !active?.root.contains(link) || link.dataset.productId === active.current.id) return;
    loadPage(link.href, link.dataset.productId, AbortSignal.timeout(15000)).catch(() => {});
  };
  document.addEventListener('pointerover', prefetch);
  document.addEventListener('focusin', prefetch);
  window.addEventListener('popstate', () => {
    if (!active || location.href === active.shownUrl) return;
    const selected = new URL(location.href).searchParams.get('variant');
    const product = active.products().find((item) => item.variants.some((variant) => variant.id === selected));
    if (product) navigate(location.href, product.id, 'pop');
  });
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', () => initialize());
})();
